import React from 'react'
import {
  Box,
  Button,
  darken,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core'
import { Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { ProviderFragment, ProviderType } from '../../../generated/graphql'
import {
  formatPrimaryParameter,
  formatSecondaryParameter,
  getPrimaryParameterTranslationKey,
  getSecondaryParameterTranslationKey,
  hasSecondaryParameter,
} from '../../../provider/provider-parameter-formatters'
import { Section } from '../../common/Section'
import { EnergyType } from '../../../calculators/EnergyFlow'
import { calculatorVar } from '../../../apollo/reactive-vars'

const useStyles = makeStyles((theme) => ({
  provider: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    height: 170,
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: darken(theme.palette.background.paper, 0.02),
    },
  },
  disabledProvider: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    height: 170,
  },
  providerType: {
    wordBreak: 'break-all',
  },
  providerParameterName: {
    textTransform: 'none',
  },
  providerParameterValue: {
    color: theme.palette.grey[500],
  },
}))

type Props = {
  providers: ProviderFragment[]
  onAdd: () => void
  onEdit: (provider: ProviderFragment) => void
  editDisabled: boolean
  energyType: EnergyType
}

export function ProviderList({
  providers,
  onAdd,
  onEdit,
  editDisabled,
  energyType,
}: Props): React.ReactElement {
  const classes = useStyles()
  const { t } = useTranslation()
  const calculator = useReactiveVar(calculatorVar)

  const providerCalculators = calculator
    ?.getAllContainers()
    .flatMap((container) => container.getProviders())

  const getProviderCalculator = (provider: ProviderFragment) =>
    providerCalculators?.find((p) => p.id === provider.id)

  const hybridHeatPumpExists = providers.some(
    ({ type }) => type === ProviderType.HybridHeatPump,
  )
  const solarPanelsExist = providers.some(
    ({ type }) => type === ProviderType.SolarPanel,
  )

  return (
    <Section
      title={t('building.tabs.energySystem.providers')}
      addon={
        <Button
          id="add-provider"
          variant="contained"
          color="primary"
          startIcon={<Plus />}
          onClick={onAdd}
          disabled={editDisabled}
        >
          {t('building.tabs.energySystem.addProvider')}
        </Button>
      }
    >
      <Grid container spacing={2}>
        {providers.map((provider) => (
          <Grid key={provider.id} item sm={6} lg={4}>
            <Paper
              className={
                editDisabled ? classes.disabledProvider : classes.provider
              }
              variant="elevation"
              elevation={0}
              tabIndex={0}
              onClick={() => (editDisabled ? undefined : onEdit(provider))}
            >
              <Typography variant="h5" className={classes.providerType}>
                {provider.name}
              </Typography>
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <Typography
                    variant="subtitle1"
                    className={classes.providerParameterName}
                  >
                    {t(getPrimaryParameterTranslationKey(provider.type))}
                  </Typography>
                  <Typography className={classes.providerParameterValue}>
                    {formatPrimaryParameter(provider)}
                  </Typography>
                </Box>
                {hasSecondaryParameter(provider) && (
                  <Box textAlign="right">
                    <Typography
                      variant="subtitle1"
                      className={classes.providerParameterName}
                    >
                      {t(
                        getSecondaryParameterTranslationKey(
                          provider.type,
                          energyType,
                        ),
                      )}
                    </Typography>
                    <Typography className={classes.providerParameterValue}>
                      {formatSecondaryParameter(
                        getProviderCalculator(provider),
                        energyType,
                        providerCalculators,
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
      {hybridHeatPumpExists && (
        <Box p={2}>
          <Typography variant="caption">
            * {t(`report.providersProvidedCaption.${energyType}`)}
          </Typography>
        </Box>
      )}
      {solarPanelsExist && (
        <Box p={2}>
          <Typography variant="caption">
            * {t(`report.providersProvidedCaption.solarPanels`)}
          </Typography>
        </Box>
      )}
    </Section>
  )
}
